import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, ChevronDown } from "lucide-react";

/**
 * QCIncomingPicker
 * Unified searchable picker across Items, POs, and GRNs.
 * Calls GET /api/qc/incoming-search?q=...
 * Props: value ({ type, id, code, name }?), onPick(entry), testId
 */
export default function QCIncomingPicker({ value, onPick, testId = "qc-incoming-picker" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get(`/qc/incoming-search?q=${encodeURIComponent(q)}`)
        .then(({ data }) => setRows(data))
        .catch(() => setRows([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const label = value ? `${value.code || ""} · ${value.name || ""}` : "";

  return (
    <div className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between border border-input rounded-md px-3 h-10 text-sm bg-background hover:bg-accent/40"
        data-testid={`${testId}-trigger`}
      >
        <span className={value ? "" : "text-muted-foreground"}>
          {value ? label : "Search Item name / PO number / GRN number…"}
        </span>
        <ChevronDown className="w-4 h-4 opacity-60 flex-shrink-0" />
      </button>
      {open ? (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-xl max-h-80 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 border-b border-border px-2 py-1">
            <Search className="w-4 h-4 text-muted-foreground" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type item name, PO number or GRN number…"
              className="border-0 h-8 focus-visible:ring-0"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="overflow-y-auto max-h-72">
            {rows.length === 0 ? (
              <div className="p-3 text-center text-xs text-muted-foreground">No results. Try a different keyword.</div>
            ) : null}
            {rows.map((r) => (
              <button
                key={`${r.type}-${r.id}`}
                type="button"
                onClick={() => { onPick(r); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex items-center justify-between"
                data-testid={`${testId}-option-${r.type}-${r.code}`}
              >
                <div>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{r.code}{r.sub ? ` · ${r.sub}` : ""}</div>
                </div>
                <Badge variant="outline" className={`font-mono text-[9px] ${r.type === "ITEM" ? "bg-blue-50" : r.type === "PO" ? "bg-amber-50" : "bg-emerald-50"}`}>{r.type}</Badge>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
